/**
 * Mock API responses for credit endpoints when Credit Mock is enabled via dev menu.
 * Shapes match backend contracts; used by CreditMockInterceptor only.
 * 12 months of data with variation across fair/good range for chart and movement indicator testing.
 */

/** 1st of the month N months ago (0 = current month). */
function monthStart(monthsAgo: number): number {
  const d = new Date();
  d.setMonth(d.getMonth() - monthsAgo);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Score range key for a given score (fair 580–669, good 670–739, etc.). */
function scoreRange(score: number): string {
  if (score >= 748) return 'credit_score_excellent';
  if (score >= 718) return 'credit_score_very_good';
  if (score >= 670) return 'credit_score_good';
  if (score >= 580) return 'credit_score_fair';
  return 'credit_score_poor';
}

// 12 months of scores, newest to oldest: varied trend (starts ~612, dips, then recovers to ~658)
// Use 1st of each month so the chart always gets 6 distinct months for the last 6 entries.
const scoresNewestFirst = [658, 652, 648, 641, 638, 635, 642, 648, 645, 639, 618, 612];
const scoreHistoryEntries = scoresNewestFirst.map((score, i) => {
  const prevScore = scoresNewestFirst[i + 1];
  const creditScoreChange = prevScore != null ? String(score - prevScore) : 'N/A';
  return {
    score,
    reportDate: monthStart(i),
    creditScoreChange,
    scoreRange: scoreRange(score),
  };
});

export const MOCK_CREDIT_SUMMARY = {
  scoreHistory: scoreHistoryEntries,
  creditScore: {
    score: scoresNewestFirst[0],
    reportDate: monthStart(0),
    creditScoreChange: String(scoresNewestFirst[0] - (scoresNewestFirst[1] ?? scoresNewestFirst[0])),
    scoreRange: scoreRange(scoresNewestFirst[0]),
  },
  prompt: {
    label: 'credit_score_prompt_title',
    text: 'credit_score_prompt_six_sixty_one_to_seven_fifteen',
  },
  factors: [
    {
      label: 'credit_factors_on_time_payments_label',
      descriptionText: 'credit_factors_on_time_payments_description',
      improvementText: 'credit_factors_on_time_payments_hundred_improvement',
      value: '92%',
    },
    {
      label: 'credit_factors_age_of_credit_label',
      descriptionText: 'credit_factors_age_of_credit_description',
      improvementText: 'credit_factors_age_of_credit_improvement',
      value: '4 yr 2 mo',
    },
    {
      label: 'credit_factors_usage_label',
      descriptionText: 'credit_factors_usage_description',
      improvementText: 'credit_factors_usage_improvement',
      value: '18%',
    },
    {
      label: 'credit_factors_derogatory_marks_label',
      descriptionText: 'credit_factors_derogatory_marks_description',
      improvementText: 'credit_factors_derogatory_marks_zero_improvement',
      value: '1',
    },
    {
      label: 'credt_factors_hard_inquiries_label',
      descriptionText: 'credit_factors_hard_inquiries_description',
      improvementText: 'credit_factors_hard_inquiries_improvement',
      value: '2',
    },
    {
      label: 'credit_factors_total_accounts_label',
      descriptionText: 'credit_factors_total_accounts_description',
      improvementText: 'credit_factors_total_accounts_improvement',
      value: '6',
    },
  ],
};

export const MOCK_CREDIT_SUBSCRIPTION = {
  statusCode: 200,
  status: 'FULFILLED',
};

/** Used when on credit-setup route so InSetupGuard allows access (does not redirect to dashboard). */
export const MOCK_CREDIT_SUBSCRIPTION_NOT_STARTED = {
  statusCode: 200,
  status: 'NOT_STARTED',
};
